<template>
  <div class="uppy-wrapper">
    <div ref="dashboardContainer"></div>
  </div>
</template>

<script>
import Uppy from "@uppy/core";
import Dashboard from "@uppy/dashboard";
// import GoogleDrive from "@uppy/google-drive";
import OneDrive from "@uppy/onedrive";
import Box from "@uppy/box";
import Dropbox from "@uppy/dropbox";
import Webcam from "@uppy/webcam";
// import GoogleDrivePicker from "@uppy/google-drive-picker";
import GoogleDrivePicker from "./uppy_google_picker";
import { COMPANION_URL, COMPANION_ALLOWED_HOSTS } from "@uppy/transloadit";
import Transloadit from "@uppy/transloadit";

import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import { createAssemblyConfig } from "@/api";
import { SUPPORTED_UPLOAD_SERVICES } from "@/constants";

export default {
  props: {
    // Upload path
    path: {
      type: String, // "free-tools", "essays", "source-materials"
      required: true,
    },

    value: {
      type: Array,
      required: true,
    },

    maxFileSizeInBytes: {
      type: Number,
      default: 10000000,
    },

    maxNumberOfFiles: {
      type: Number,
      default: 1,
    },

    acceptFiles: {
      type: Array,
      default: () => [
        ".pdf",
        ".txt",
        ".docx",
        ".png",
        ".jpg",
        ".jpeg",
        ".html",
        "application/vnd.google-apps.document",
      ],
    },

    supportedUploadServices: {
      type: Array,
      default: () => SUPPORTED_UPLOAD_SERVICES,
    },

    pluginNameCameraTitle: {
      type: String,
      default: "Webcam",
    },
  },

  data() {
    return {
      uppy: null,
      payload: null,
      disabled: true,
    };
  },
  mounted() {
    this.payload = this.value;
    this.initUppy();
  },

  methods: {
    initUppy() {
      this.uppy = new Uppy({
        // needed because GoogleDrivePickerView use global uppy locales,
        // then used old plugin key pluginNameGoogleDrive not a pluginNameGoogleDrivePicker
        locale: {
          strings: {
            pluginNameGoogleDrive: "Google Drive",
            youCanOnlyUploadFileTypes:
              "You can only upload Word, Text, PDF, Google Docs, HTML or Image files (.png, .jpg, .jpeg).",
          },
        },
        debug: process.env.NODE_ENV === "development",
        autoProceed: false,
        restrictions: {
          maxFileSize: this.maxFileSizeInBytes,
          minNumberOfFiles: 1,
          maxNumberOfFiles: this.maxNumberOfFiles,
          allowedFileTypes: this.acceptFiles,
        },
      })
        .use(Dashboard, {
          hideUploadButton: true,
          inline: true,
          height: "100%",
          width: "100%",
          target: this.$refs.dashboardContainer,
          replaceTargetContent: true,
          showProgressDetails: true,
          browserBackButtonClose: true,
        })
        .use(Transloadit, {
          waitForEncoding: true,
          assemblyOptions: async () => {
            const response = await createAssemblyConfig({
              path: this.path,
            });

            return response.data.assembly;
          },
        });

      const companionUrl = COMPANION_URL;
      const companionAllowedHosts = COMPANION_ALLOWED_HOSTS;

      if (this.supportedUploadServices.includes("googleDrive")) {
        this.uppy.use(GoogleDrivePicker, {
          companionUrl,
          companionAllowedHosts,
          clientId: process.env.VUE_APP_CLIENT_ID,
          apiKey: process.env.VUE_APP_GDRIVE_PICKER_API_KEY,
          appId: process.env.VUE_APP_GOOGLE_APP_ID,
          locale: {
            strings: {
              pluginNameGoogleDrive: "Google Drive",
            },
          },
        });
      }

      if (this.supportedUploadServices.includes("oneDrive")) {
        this.uppy.use(OneDrive, {
          companionUrl,
          companionAllowedHosts,
        });
      }

      if (this.supportedUploadServices.includes("box")) {
        this.uppy.use(Box, {
          companionUrl,
          companionAllowedHosts,
        });
      }

      if (this.supportedUploadServices.includes("dropbox")) {
        this.uppy.use(Dropbox, {
          companionUrl,
          companionAllowedHosts,
        });
      }

      if (this.supportedUploadServices.includes("webcam")) {
        this.uppy.use(Webcam, {
          locale: {
            strings: {
              pluginNameCamera: this.pluginNameCameraTitle,
            },
          },
          modes: "picture",
          companionUrl,
          companionAllowedHosts,
          mirror: false,
          videoConstraints: {
            facingMode: "environment",
          },
        });
      }

      this.uppy.on("file-added", () => {
        this.$emit("file-added", this.uppy.getFiles());
      });

      this.uppy.on("file-removed", () => {
        this.$emit("file-removed", this.uppy.getFiles());
      });

      this.uppy.on("transloadit:complete", (assembly) => {
        this.$emit(
          "input",
          assembly.results[":original"].map((file) => ({
            fileUrl: file.ssl_url,
            name: file.name,
          }))
        );
        this.disabled = false;
      });
    },

    upload() {
      return this.uppy.upload();
    },

    resetUploader() {
      this.uppy.reset();
      this.disabled = true;

      return this;
    },
  },
};
</script>

<style scoped lang="scss">
.image-container {
  height: 150px;
  width: 150px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: auto;
  margin-left: auto;
}

.image-container > img {
  width: inherit;
  height: inherit;
}

.uppy-wrapper::v-deep {
  z-index: 4;

  .uppy-Dashboard-inner {
    border: none;
    background-color: rgba(111, 66, 193, 0.1);
  }

  .uppy-Dashboard-AddFiles {
    border-color: #6f42c1;
    height: 100%;
    min-height: var(--uppy-dashboard-area-min-height);
    margin: 0;
    border-radius: 5px;
  }

  .uppy-Dashboard-AddFiles-title {
    font-size: 16px;
    color: #212529;
  }

  .uppy-DashboardTab-name {
    font-size: 14px;
    color: #212529;
  }

  .uppy-DashboardContent-back:focus,
  .uppy-DashboardContent-save:focus,
  .uppy-DashboardTab-btn:hover {
    background-color: rgba(111, 66, 193, 0.2);
  }

  .uppy-Provider-btn-google,
  .uppy-c-btn-primary {
    background-color: #6f42c1;
  }

  .uppy-Webcam-permissonsIcon svg,
  .uppy-Webcam-button svg,
  .uppy-DashboardTab-btn[data-cy="Webcam"] path {
    fill: #6f42c1;
  }

  .uppy-DashboardTab-iconMyDevice,
  .uppy-DashboardContent-addMore,
  .uppy-ProviderBrowser-userLogout,
  .uppy-DashboardContent-back {
    color: #6f42c1;
  }

  .uppy-ProviderBrowser-viewType--list
    .uppy-ProviderBrowserItem--is-checked
    .uppy-ProviderBrowserItem-checkbox,
  .uppy-ProviderBrowser-viewType--list
    .uppy-ProviderBrowserItem--is-partial
    .uppy-ProviderBrowserItem-checkbox {
    background-color: #6f42c1;
    border-color: #6f42c1;
  }

  .uppy-StatusBar.is-complete {
    display: none;
  }

  .uppy-DashboardContent-panelBody {
    height: calc(100% - 60px);
    margin-top: 8px;
    align-items: inherit;
  }

  .uppy-Webcam-container {
    height: calc(100% - 60px);
  }

  .uppy-Webcam-videoContainer {
    height: 100%;
  }

  .uppy-Webcam-video {
    height: 100%;
    border-radius: 8px;
    background-color: whitesmoke;
    box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.1),
      0 2px 3px 0 rgba(0, 0, 0, 0.02);
  }

  .uppy-Webcam-footer {
    text-align: center;
  }

  .uppy-Webcam-button {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 8px auto;
    width: 44px;
    height: 44px;
    padding: 6px;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.1),
      0 2px 3px 0 rgba(0, 0, 0, 0.02);
  }

  .uppy-Webcam-permissons {
    margin: auto;
    width: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
  }
}
</style>
